import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { programsApi } from '@/utils/api';
import type { Program, ApiResponse, LeadType } from '@/types';
import { ISCategory } from '@/types';

export interface ProgramAnalytics {
  program: {
    id: string;
    name: string;
    isActive: boolean;
  };
  capacity: {
    totalTeams: number;
    activeTeams: number;
    totalAllocatedTW: number;
    totalResources: number;
    totalAnnualCost: number;
  };
  resources: {
    total: number;
    byType: Record<string, number>;
    byLevel: Record<string, number>;
  };
  epics: {
    primary: {
      count: number;
      statusBreakdown: Record<string, number>;
      byCategoryFromIS: Record<string, number>;
      totalFeatures: number;
    };
    contributing: {
      count: number;
    };
  };
  features: {
    total: number;
    statusBreakdown: Record<string, number>;
  };
  teams: Array<{
    teamId: string;
    teamName: string;
    resourceCount: number;
    annualCost: number;
    costProfile: string;
  }>;
  trends: {
    allocations: Array<{
      period: string;
      totalTW: number;
      epicCount: number;
    }>;
  };
}

interface ProgramsState {
  programs: Program[];
  currentProgram: Program | null;
  analytics: ProgramAnalytics | null;
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isValidatingLead: boolean;
  isLoadingAnalytics: boolean;
  error: string | null;
  leadValidationError: string | null;
  filters: {
    isActive?: boolean;
    leadType?: LeadType;
  };
}

const initialState: ProgramsState = {
  programs: [],
  currentProgram: null,
  analytics: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isValidatingLead: false,
  isLoadingAnalytics: false,
  error: null,
  leadValidationError: null,
  filters: {},
};

// Async thunks
export const fetchPrograms = createAsyncThunk(
  'programs/fetchAll',
  async (params: { isActive?: boolean } = {}, { rejectWithValue }) => {
    // Remove category filter since all programs are TECH-only
    const response = await programsApi.getAll(params);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch programs');
    }
    return response.data;
  }
);

export const fetchProgramById = createAsyncThunk(
  'programs/fetchById',
  async (id: string, { rejectWithValue }) => {
    const response = await programsApi.getById(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch program');
    }
    return response.data;
  }
);

export const createProgram = createAsyncThunk(
  'programs/create',
  async (data: Partial<Program>, { rejectWithValue }) => {
    // Ensure category is always TECH
    const programData = { ...data, category: ISCategory.TECH };
    const response = await programsApi.create(programData);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to create program');
    }
    return response.data;
  }
);

export const updateProgram = createAsyncThunk(
  'programs/update',
  async ({ id, data }: { id: string; data: Partial<Program> }, { rejectWithValue }) => {
    // Ensure category is always TECH
    const programData = { ...data, category: ISCategory.TECH };
    const response = await programsApi.update(id, programData);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to update program');
    }
    return response.data;
  }
);

export const deleteProgram = createAsyncThunk(
  'programs/delete',
  async (id: string, { rejectWithValue }) => {
    const response = await programsApi.delete(id);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to delete program');
    }
    return id;
  }
);

// New thunk for validating lead assignment
export const validateLeadAssignment = createAsyncThunk(
  'programs/validateLead',
  async ({ leadType, leadId, leadText }: {
    leadType: LeadType;
    leadId?: string;
    leadText?: string;
  }, { rejectWithValue }) => {
    try {
      // Validate lead assignment consistency
      if (leadType === 'USER' && !leadId) {
        return rejectWithValue('User ID is required when lead type is USER');
      }
      if (leadType === 'TEXT' && !leadText?.trim()) {
        return rejectWithValue('Lead text is required when lead type is TEXT');
      }
      if (leadType === 'USER' && leadText) {
        return rejectWithValue('Cannot specify both user ID and lead text');
      }
      if (leadType === 'TEXT' && leadId) {
        return rejectWithValue('Cannot specify both lead text and user ID');
      }

      return { valid: true };
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Lead validation failed');
    }
  }
);

// Fetch program analytics
export const fetchProgramAnalytics = createAsyncThunk(
  'programs/fetchAnalytics',
  async (programId: string, { rejectWithValue }) => {
    const response = await programsApi.getAnalytics(programId);
    if (!response.success) {
      return rejectWithValue(response.error?.message || 'Failed to fetch program analytics');
    }
    return response.data;
  }
);

const programsSlice = createSlice({
  name: 'programs',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLeadValidationError: (state) => {
      state.leadValidationError = null;
    },
    setFilters: (state, action: PayloadAction<Partial<ProgramsState['filters']>>) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    setCurrentProgram: (state, action: PayloadAction<Program | null>) => {
      state.currentProgram = action.payload;
    },
    updateCurrentProgramLead: (state, action: PayloadAction<{
      leadType: LeadType;
      leadId?: string;
      leadText?: string;
    }>) => {
      if (state.currentProgram) {
        state.currentProgram.leadType = action.payload.leadType;
        state.currentProgram.leadId = action.payload.leadId;
        state.currentProgram.leadText = action.payload.leadText;
      }
    },
    ensureTechCategory: (state) => {
      // Ensure all programs in state have TECH category
      state.programs.forEach(program => {
        if (program.category !== ISCategory.TECH) {
          program.category = ISCategory.TECH;
        }
      });
      if (state.currentProgram && state.currentProgram.category !== ISCategory.TECH) {
        state.currentProgram.category = ISCategory.TECH;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchPrograms.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPrograms.fulfilled, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as { programs?: Program[] } | Program[];
        state.programs = (payload && typeof payload === 'object' && 'programs' in payload ? payload.programs : payload) || [];
      })
      .addCase(fetchPrograms.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchProgramById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProgramById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProgram = action.payload as Program;
      })
      .addCase(fetchProgramById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createProgram.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createProgram.fulfilled, (state, action) => {
        state.isCreating = false;
        // Backend returns { program: ... }, extract the program
        const payload = action.payload as { program?: Program } | Program;
        const program = (payload && typeof payload === 'object' && 'program' in payload ? payload.program : payload) as Program;
        state.programs.unshift(program);
      })
      .addCase(createProgram.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateProgram.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateProgram.fulfilled, (state, action) => {
        state.isUpdating = false;
        // Backend returns { program: ... }, extract the program
        const payload = action.payload as { program?: Program } | Program;
        const program = (payload && typeof payload === 'object' && 'program' in payload ? payload.program : payload) as Program;
        const index = state.programs.findIndex(p => p.id === program.id);
        if (index !== -1) {
          state.programs[index] = program;
        }
        if (state.currentProgram?.id === program.id) {
          state.currentProgram = program;
        }
      })
      .addCase(updateProgram.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete
      .addCase(deleteProgram.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteProgram.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.programs = state.programs.filter(p => p.id !== action.payload);
        if (state.currentProgram?.id === action.payload) {
          state.currentProgram = null;
        }
      })
      .addCase(deleteProgram.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Validate lead assignment
      .addCase(validateLeadAssignment.pending, (state) => {
        state.isValidatingLead = true;
        state.leadValidationError = null;
      })
      .addCase(validateLeadAssignment.fulfilled, (state) => {
        state.isValidatingLead = false;
        state.leadValidationError = null;
      })
      .addCase(validateLeadAssignment.rejected, (state, action) => {
        state.isValidatingLead = false;
        state.leadValidationError = action.payload as string;
      })
      // Fetch analytics
      .addCase(fetchProgramAnalytics.pending, (state) => {
        state.isLoadingAnalytics = true;
        state.error = null;
      })
      .addCase(fetchProgramAnalytics.fulfilled, (state, action) => {
        state.isLoadingAnalytics = false;
        const payload = action.payload as any;
        state.analytics = payload.analytics || payload;
      })
      .addCase(fetchProgramAnalytics.rejected, (state, action) => {
        state.isLoadingAnalytics = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  clearLeadValidationError,
  setFilters, 
  clearFilters, 
  setCurrentProgram,
  updateCurrentProgramLead,
  ensureTechCategory
} = programsSlice.actions;

export default programsSlice.reducer;